/**
 * Amplify v6 Compatibility Layer
 *
 * This module provides a compatibility wrapper for Amplify v5 API using v6 under the hood.
 * This allows existing code to continue working while we migrate to v6 incrementally.
 *
 * Migration Status: Temporary compatibility layer
 * TODO: Migrate all code to use Amplify v6 APIs directly and remove this file
 */

import * as AmplifyAuth from 'aws-amplify/auth';

/**
 * Compatibility wrapper for Auth.currentSession()
 * v5: Auth.currentSession()
 * v6: fetchAuthSession()
 */
async function currentSession() {
  try {
    const session = await AmplifyAuth.fetchAuthSession();

    // Transform v6 session to v5 format
    return {
      accessToken: {
        jwtToken: session.tokens?.accessToken?.toString(),
        payload: session.tokens?.accessToken?.payload,
      },
      idToken: {
        jwtToken: session.tokens?.idToken?.toString(),
        payload: session.tokens?.idToken?.payload,
      },
      getAccessToken: () => ({
        getJwtToken: () => session.tokens?.accessToken?.toString(),
        payload: session.tokens?.accessToken?.payload,
      }),
      getIdToken: () => ({
        getJwtToken: () => session.tokens?.idToken?.toString(),
        payload: session.tokens?.idToken?.payload,
      }),
    };
  } catch (error) {
    console.error('Error in currentSession compat:', error);
    throw error;
  }
}

/**
 * Compatibility wrapper for Auth.signIn()
 * v5: Auth.signIn(username, password)
 * v6: signIn({ username, password })
 */
async function signIn(username, password) {
  try {
    console.log('[AmplifyCompat] signIn called with username:', username);

    // Check if there's already a signed-in user and sign them out first
    try {
      const currentUser = await AmplifyAuth.getCurrentUser();
      if (currentUser) {
        console.log('[AmplifyCompat] User already authenticated, signing out first:', currentUser.username);
        await AmplifyAuth.signOut();
        console.log('[AmplifyCompat] Previous session signed out successfully');
      }
    } catch (checkError) {
      // No user signed in, continue with sign in
      console.log('[AmplifyCompat] No existing session found, proceeding with sign in');
    }

    const result = await AmplifyAuth.signIn({
      username: username.trim(),
      password,
    });

    console.log('[AmplifyCompat] signIn result:', result);

    // Check if additional steps are required
    if (result.isSignedIn) {
      // Get user attributes to match v5 behavior
      const attributes = await AmplifyAuth.fetchUserAttributes();
      console.log('[AmplifyCompat] User signed in successfully, attributes:', attributes);
      return {
        username,
        attributes,
        signInUserSession: await currentSession(),
      };
    }

    // Handle challenge flows (NEW_PASSWORD_REQUIRED, etc.)
    if (result.nextStep) {
      const challengeName = result.nextStep.signInStep;
      console.log('[AmplifyCompat] Challenge required:', challengeName);

      // Map v6 challenge names to v5 format
      const challengeMap = {
        CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
        CONFIRM_SIGN_IN_WITH_SMS_CODE: 'SMS_MFA',
        CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'SOFTWARE_TOKEN_MFA',
      };

      return {
        username,
        challengeName: challengeMap[challengeName] || challengeName,
        challengeParam: result.nextStep.additionalInfo || {},
      };
    }

    console.log('[AmplifyCompat] Unexpected result format:', result);
    return result;
  } catch (error) {
    console.error('[AmplifyCompat] signIn error:', error);
    console.error('[AmplifyCompat] error.name:', error.name);
    console.error('[AmplifyCompat] error.message:', error.message);

    // Map v6 errors to v5 error codes
    const errorMap = {
      NotAuthorizedException: 'NotAuthorizedException',
      UserNotFoundException: 'UserNotFoundException',
      InvalidParameterException: 'InvalidParameterException',
    };

    const mappedError = new Error(error.message);
    mappedError.code = errorMap[error.name] || error.name;
    mappedError.name = error.name;

    console.error('[AmplifyCompat] Throwing mapped error:', mappedError);
    throw mappedError;
  }
}

/**
 * Compatibility wrapper for Auth.signOut()
 * v5: Auth.signOut()
 * v6: signOut()
 */
async function signOut() {
  try {
    await AmplifyAuth.signOut();
    return 'SUCCESS';
  } catch (error) {
    console.error('Error in signOut compat:', error);
    throw error;
  }
}

/**
 * Compatibility wrapper for Auth.currentUserInfo()
 * v5: Auth.currentUserInfo()
 * v6: getCurrentUser() + fetchUserAttributes()
 */
async function currentUserInfo() {
  try {
    const user = await AmplifyAuth.getCurrentUser();
    const attributes = await AmplifyAuth.fetchUserAttributes();

    return {
      id: user.userId,
      username: user.username,
      attributes,
    };
  } catch (error) {
    console.error('Error in currentUserInfo compat:', error);
    // Return null if not authenticated (v5 behavior)
    return null;
  }
}

/**
 * Compatibility wrapper for Auth.forgotPasswordSubmit()
 * v5: Auth.forgotPasswordSubmit(username, code, newPassword)
 * v6: confirmResetPassword({ username, confirmationCode, newPassword })
 */
async function forgotPasswordSubmit(username, code, newPassword) {
  try {
    await AmplifyAuth.confirmResetPassword({
      username: username.trim(),
      confirmationCode: code,
      newPassword,
    });
    return 'SUCCESS';
  } catch (error) {
    // Map v6 errors to v5 error codes
    const errorMap = {
      CodeMismatchException: 'CodeMismatchException',
      ExpiredCodeException: 'ExpiredCodeException',
      InvalidParameterException: 'InvalidParameterException',
    };

    const mappedError = new Error(error.message);
    mappedError.code = errorMap[error.name] || error.name;
    mappedError.name = error.name;

    throw mappedError;
  }
}

/**
 * Compatibility wrapper for Auth.forgotPassword()
 * v5: Auth.forgotPassword(username)
 * v6: resetPassword({ username })
 */
async function forgotPassword(username) {
  try {
    const result = await AmplifyAuth.resetPassword({ username: username.trim(), });
    return {
      CodeDeliveryDetails: result.nextStep.codeDeliveryDetails,
    };
  } catch (error) {
    console.error('Error in forgotPassword compat:', error);
    throw error;
  }
}

/**
 * Compatibility wrapper for Auth.completeNewPassword()
 * v5: Auth.completeNewPassword(user, newPassword, requiredAttributes)
 * v6: confirmSignIn({ challengeResponse: newPassword })
 */
async function completeNewPassword(user, newPassword, requiredAttributes = {}) {
  try {
    const result = await AmplifyAuth.confirmSignIn({
      challengeResponse: newPassword,
      options: {
        userAttributes: requiredAttributes,
      },
    });

    if (result.isSignedIn) {
      const attributes = await AmplifyAuth.fetchUserAttributes();
      return {
        username: user.username,
        attributes,
        signInUserSession: await currentSession(),
      };
    }

    return result;
  } catch (error) {
    console.error('Error in completeNewPassword compat:', error);
    throw error;
  }
}

/**
 * Compatibility wrapper for Auth.federatedSignIn()
 * v5: Auth.federatedSignIn({ provider: 'Google' })
 * v6: signInWithRedirect({ provider: { custom: 'Google' } })
 */
async function federatedSignIn({ provider, }) {
  try {
    console.log('[AmplifyCompat] federatedSignIn called with provider:', provider);
    // Check if there's already a signed-in user and sign them out first
    try {
      const currentUser = await AmplifyAuth.getCurrentUser();
      if (currentUser) {
        console.log(
          '[AmplifyCompat] User already authenticated, signing out first:',
          currentUser.username
        );
        await AmplifyAuth.signOut();
        console.log('[AmplifyCompat] Previous session signed out successfully');
      }
    } catch (checkError) {
      // No user signed in, continue with sign in
      console.log('[AmplifyCompat] No existing session found, proceeding with sign in');
    }
    // Map v5 provider names to v6 format
    const providerMap = {
      Google: 'Google',
      Facebook: 'Facebook',
      Amazon: 'LoginWithAmazon',
      Apple: 'SignInWithApple',
    };

    const v6Provider = providerMap[provider] || provider;

    await AmplifyAuth.signInWithRedirect({
      provider: { custom: v6Provider, },
    });

    console.log('[AmplifyCompat] federatedSignIn redirect initiated');
  } catch (error) {
    console.error('[AmplifyCompat] federatedSignIn error:', error);
    throw error;
  }
}

/**
 * Export Auth object with v5-compatible API
 */
export const Auth = {
  currentSession,
  signIn,
  signOut,
  currentUserInfo,
  forgotPasswordSubmit,
  forgotPassword,
  completeNewPassword,
  federatedSignIn,
};

export default Auth;
